import { Component, ElementRef, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BlobType, FileModel, SingleResponse, WorkflowCategoryV1Read, WorkflowType, WorkflowUpdate, WorkflowV1Read } from '@ca/core/models';
import { UserNotificationService, WorkflowV1Service } from '@ca/core/services';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY } from 'rxjs';
import { catchError, filter, finalize, tap } from 'rxjs/operators';
import { CustomValidators } from '../../../../utils/custom-validators/custom-validators';
import { isInWorkspace } from '../../../../utils/route/route-utils';
import { SegmentationType } from '../../../core/enums/segmentation-type.enum';
import { DESCRIPTION_LENGTH_LIMIT, SUMMARY_LENGTH_LIMIT, SUPPORTED_IMAGE_TYPES, TITLE_LENGTH_LIMIT } from '../../constants/form-validators';
import { WorkflowValidators } from '../../validators/workflow-validators';

@Component({
    selector: 'ca-workflow-metadata-editor',
    templateUrl: './workflow-metadata-editor.component.html',
    styleUrls: ['./workflow-metadata-editor.component.scss'],
})
export class WorkflowMetadataEditorComponent implements OnInit {
    @ViewChildren('imageRef') imageRefList: QueryList<ElementRef>;

    @Input() asAdmin = false;
    @Input() workflow: WorkflowV1Read;
    @Input() categories: WorkflowCategoryV1Read[];
    @Input() types: { name: string; value?: WorkflowType }[];
    @Input() segmentationTypes: { name: string; value?: SegmentationType }[];
    @Input() isPublicWorkflow: boolean;

    readonly descriptionLengthLimit: number = DESCRIPTION_LENGTH_LIMIT;
    readonly summaryLengthLimit = SUMMARY_LENGTH_LIMIT;
    readonly titleLengthLimit = TITLE_LENGTH_LIMIT;

    readonly maxInputSizeInBytes = 1024 * 1024;
    readonly supportedImageTypes: string[] = SUPPORTED_IMAGE_TYPES;

    formGroup: UntypedFormGroup;
    isSubmitting = false;
    workflowImageBlobType: BlobType = BlobType.WorkflowImage;
    readonly maxImages = 3;

    get description(): AbstractControl {
        return this.formGroup.get('description') as AbstractControl;
    }

    // list of filenames, map '/blob/store/path/file-name.jpg' => 'file-name.jpg'
    get imageFileNames(): string[] {
        return (this.formGroup?.get('images') as UntypedFormArray).controls.map((ctrl: UntypedFormControl) => ctrl.value?.split('/')?.pop()) || [];
    }

    get maxImagesFull(): boolean {
        return (this.formGroup?.get('images') as UntypedFormArray)?.length >= this.maxImages;
    }

    get isInWorkspace(): boolean {
        return isInWorkspace(this.router.url);
    }

    constructor(
        private router: Router,
        private formBuilder: UntypedFormBuilder,
        private workflowV1Service: WorkflowV1Service,
        private userNotificationService: UserNotificationService,
        private activeModal: NgbActiveModal,
    ) {}

    ngOnInit(): void {
        this.buildForm();
    }

    submit(): void {
        if (this.formGroup.invalid) {
            return;
        }

        this.isSubmitting = true;
        this.userNotificationService.showSpinner();

        this.workflowV1Service
            .update(this.workflow.id, this.getUpdateData(), this.asAdmin)
            .pipe(
                filter(({ data }: SingleResponse<WorkflowV1Read>) => data != null),
                tap(({ data }: SingleResponse<WorkflowV1Read>) => {
                    this.userNotificationService.showSuccess('WORKFLOWS.METADATA_EDITOR.SAVE_SUCCESS_MSG');
                    this.activeModal.close(data);
                }),
                catchError(() => {
                    this.userNotificationService.showError('GENERAL.CHANGED_FAILURE');
                    return EMPTY;
                }),
                finalize(() => {
                    this.isSubmitting = false;
                    this.userNotificationService.hideSpinner();
                }),
            )
            .subscribe();
    }

    dismiss(reason?: string): void {
        this.activeModal.dismiss(reason);
    }

    onTouched(): void {
        this.description.markAsTouched();
    }

    onUploadCompleted({ token }: FileModel): void {
        if (token && !this.maxImagesFull) {
            (this.formGroup.get('images') as UntypedFormArray)!.push(new UntypedFormControl(token));
        }
    }

    removeFileItem(index: number): void {
        this.imageRefList.get(index)?.nativeElement.remove();
        (this.formGroup.get('images') as UntypedFormArray)!.removeAt(index);
    }

    private getUpdateData(): WorkflowUpdate {
        return {
            title: this.formGroup.value.title.trim(),
            description: this.formGroup.value.description,
            summary: this.formGroup.value.summary,
            categoryId: this.formGroup.value.categoryId,
            type: this.formGroup.value.type,
            segmentationTypeSupported: this.formGroup.value.segmentationTypeSupported,
            images: this.formGroup.value.images,
            isDefaultForType: this.formGroup.value.isDefaultForType,
            ...(this.asAdmin && this.isPublicWorkflow ? { isFeatured: this.formGroup.value.isFeatured } : {}),
            ...(this.asAdmin && this.isPublicWorkflow && this.formGroup.value.type != WorkflowType.UNDEFINED
                ? { mlVersion: this.formGroup.value.mlVersion }
                : {}),
        };
    }

    private buildForm(): void {
        if (!this.workflow) {
            return;
        }

        this.formGroup = this.formBuilder.group(
            {
                title: [this.workflow.title, CustomValidators.ModelTitle()],
                summary: [this.workflow.summary, CustomValidators.SummaryValidation()],
                description: [
                    this.workflow.description,
                    [CustomValidators.requiredWithoutHtmlTags(), CustomValidators.maxLengthWithoutHtmlTags(DESCRIPTION_LENGTH_LIMIT)],
                ],
                categoryId: [this.workflow?.category?.id],
                type: this.workflow.type,
                segmentationTypeSupported: this.workflow.segmentationTypeSupported,
                images: this.formBuilder.array(this.workflow.images || []),
                isDefaultForType: this.workflow.isDefaultForType,
                isFeatured: this.workflow.isFeatured,
                mlVersion: this.workflow.mlVersion,
            },
            { validators: WorkflowValidators.defaultType(this.workflow.isDefaultForType ?? false, this.workflow.type) },
        );
    }
}
